<template>
  <div>
    <aha-section>
      <template #header>
        <h5 v-if="!data.oneTime">
          Current plan
        </h5>
        <h5 v-else>
          On-going plan
        </h5>
      </template>
      <template #body>
        <table class="table-plan">
          <colgroup>
            <col class="w-1/2 md:w-1/3">
            <col class="w-1/2 md:w-2/3">
          </colgroup>
          <tbody class="w-full">
            <tr
              v-if="data.subscription.plan.group != 'pro'
                && data.subscription.plan.group != 'white-label'"
            >
              <td colspan="2" class="py-2 italic">
                You can upgrade to AhaSlides Pro to host
                bigger events, have customised branding and more!
              </td>
            </tr>
            <tr>
              <td class="table-plan-property">
                <i class="mr-1 fas fa-cart-arrow-down fa-fw" />
                Your plan:
              </td>
              <td
                class="py-2 font-bold table-plan-value"
                :class="getPlanColor(data.subscription.plan)"
              >
                {{ data.subscription.plan.name }}
              </td>
            </tr>
            <tr>
              <td class="table-plan-property">
                <i class="mr-1 fas fa-user fa-fw" />

                Owner:
              </td>
              <td class="py-2 table-plan-value">
                {{ currentUser.email }}
              </td>
            </tr>
            <tr>
              <td class="table-plan-property">
                <i class="mr-1 fas fa-users fa-fw" />
                Participant limit:
              </td>
              <td class="py-2 table-plan-value">
                {{ data.subscription.plan.limit | formatNumber }}
              </td>
            </tr>
            <tr v-if="data.subscription && data.subscription.current_period_start">
              <td class="table-plan-property">
                <i class="mr-1 far fa-calendar-alt fa-fw" />
                Start date:
              </td>
              <td class="py-2 table-plan-value">
                {{ data.subscription.current_period_start | dateTimeStamp }}
              </td>
            </tr>
            <tr v-if="data.subscription">
              <td class="table-plan-property">
                <i class="mr-1 far fa-calendar-check fa-fw" />
                <span
                  v-if="data.subscription.status == 'active' &&
                    !data.subscription.cancel_at_period_end"
                >
                  Next billing date:
                </span>
                <span v-else>
                  End date:
                </span>
              </td>
              <td class="py-2 table-plan-value">
                {{ data.subscription.current_period_end | dateTimeStamp }}
              </td>
            </tr>
            <tr
              v-if="data.subscription && $store.state.plan.type != 'white-label'
                && data.subscription.amount"
            >
              <td class="capitalize table-plan-property">
                <i class="mr-1 fas fa-money-check-alt fa-fw" />
                {{ data.subscription.plan.billingCycle }} cost:
              </td>
              <td class="py-2 table-plan-value" v-if="isDisplayingNextInvoicePromotion">
                <span
                  class="pr-1 line-through sub-has-coupon text-base-60"
                >{{ data.subscription.amount }} USD</span>
                <span>{{ promotionOnCancelAmount }} USD</span>
                <span
                  v-if="isDisplayingNextInvoicePromotion"
                >
                  <aha-badge
                    :label="`${promotionOnCancelPercent}% OFF`"
                    class="inline mx-2 bg-green-50"
                  />
                  <span
                    class="cursor-pointer fas fa-question-circle text-secondary-40"
                    v-tooltip="promotionOnCancelTooltip"
                  />
                </span>
              </td>
              <td class="py-2 table-plan-value" v-else>
                {{ data.subscription.amount }} USD
                <p v-if="$store.state.plan.oldPlan" class="text-xs">
                  Although the price for this plan has increased after you subscribed,
                  you are still paying the old price for as long as your subscription goes.
                </p>
              </td>
            </tr>
            <tr
              v-if="data.subscription && data.subscription.cancel_at_period_end"
            >
              <td class="table-plan-property">
                <i class="mr-1 fas fa-undo-alt fa-fw" />
                Auto-renewal:
              </td>
              <td class="py-2 table-plan-value">
                <span>
                  Off <span v-if="!autoRenewOn">
                    (<a
                      class="autorenewing-text"
                      v-if="isTurningOnAutoRenew"
                    >turning on...</a>
                    <a
                      class="text-link--muted"
                      v-else
                      @click="$emit('handleTurnOnAutoRenew')"
                    >turn on</a>)
                  </span>
                </span>
              </td>
            </tr>
            <tr
              v-if="data.subscription && data.subscription.coupon &&
                $store.state.plan.type != 'white-label'"
            >
              <td class="table-plan-property">
                <i class="mr-1 fas fa-gift fa-fw" />
                Discount applied:
              </td>
              <td class="py-2 table-plan-value">
                <div>
                  <span
                    :class="{'sub-has-coupon line-through pr-1': data.subscription.coupon}"
                  >{{ data.subscription.amount }} USD</span>
                  <span
                    v-if="data.subscription.coupon"
                    class="font-bold"
                  >{{ data.subscription.coupon.amount }} USD</span>
                </div>
                <div
                  class="text-xs label-coupon text-base-80"
                >
                  Promo code: <span
                    v-if="!data.subscription.coupon.metadata ||
                      data.subscription.coupon.metadata.offer_on_cancellation !== 'true'"
                  >{{ data.subscription.coupon.id }} -</span>
                  {{ data.subscription.coupon.name }}
                </div>
                <div
                  class="text-xs label-coupon text-base-80"
                  v-if="!data.subscription.coupon.metadata ||
                    data.subscription.coupon.metadata.offer_on_cancellation !== 'true'"
                >
                  Duration: {{ data.subscription.coupon.duration }}
                  <span
                    v-if="data.subscription.coupon.duration == 'repeating'"
                  >({{ data.subscription.coupon.duration_in_months }} months)</span>
                </div>
              </td>
            </tr>
            <tr v-if="data.card">
              <td class="align-top table-plan-property">
                <i class="mr-1 far fa-credit-card fa-fw" />
                Payment method:
              </td>
              <td class="py-2 table-plan-value">
                <strong class="uppercase">{{ data.card.brand }}</strong> •••• •••• ••••
                <strong>{{ data.card.last4 }}</strong>
                <br>
                <span v-if="data.subscription" class="small-90">
                  <span
                    class="text-link--muted"
                    @click="$emit('showPopupUpdateCardInfo')"
                  >Update</span> /
                </span>
                <span
                  class="text-link--muted"
                  @click="$emit('removeCardInfo')"
                >Remove</span>
              </td>
            </tr>
            <tr
              v-if="data.subscription &&
                data.subscription.current_period_start &&
                !data.subscription.cancel_at_period_end"
            >
              <td colspan="2" class="py-2 text-sm text-left">
                <p>
                  We will send you a reminder email 2 weeks
                  before your subscription is renewed.
                  To cancel your subscription now, please
                  <span
                    class="cursor-pointer text-base-80 hover:text-primary-90"
                    @click="handleClickOnCancelSub()"
                  >click here</span>.
                </p>
              </td>
            </tr>
            <tr>
              <td class="table-plan-property">
                <aha-icon name="coin" class="-mt-0.5 -ml-0.5" />
                Credit balance:
              </td>
              <td
                class="py-2 table-plan-value"
              >
                <span v-if="currentUser.balance">
                  {{ (0 - currentUser.balance)/100 | floatNumber }} USD
                </span>
                <span v-else>0.00 USD</span>
              </td>
            </tr>
          </tbody>
        </table>
      </template>
      <template
        #footer
        v-if="showUpgradePlanFooter"
      >
        <div class="h-13 p-3 bg-yellow-10 text-yellow-100">
          <span>Upgrading your plan here will not affect your team plan.</span>
        </div>
      </template>
    </aha-section>
    <modal-cancel-sub-info
      v-if="isCancelSubInfo"
      @close="isCancelSubInfo = false"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import storageKeys from '@/constant/storageKeys'
import ModalCancelSubInfo from '../modals/CancelSubInfo.vue'

export default {
  components: {
    ModalCancelSubInfo,
  },
  props: {
    data: {
      type: Object,
      default: () => {},
    },
    isDisplayingNextInvoicePromotion: {
      type: Boolean,
      default: false,
    },
    autoRenewOn: {
      type: Boolean,
      default: false,
    },
    isTurningOnAutoRenew: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isCancelSubInfo: false,
    }
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.currentUser,
    }),
    showUpgradePlanFooter() {
      if (this.$store.state.currentUser.accountId
        && this.$store.state.currentUser.accountRole === 'owner'
        && this.$store.state.currentAccount.createdFrom === 'presenter') return true
      return false
    },
    promotionOnCancelAmount() {
      if (!this.data.discount || !this.data.discount.coupon ||
      !this.data.discount.coupon.percent_off || !this.data.subscription) return null
      return Math.round((100 - this.data.discount.coupon.percent_off)
      * this.data.subscription.amount) / 100
    },
    promotionOnCancelPercent() {
      return this.data.discount &&
         this.data.discount.coupon &&
        this.data.discount.coupon.percent_off
    },

    promotionOnCancelTooltip() {
      return `This exclusive offer takes ${this.promotionOnCancelPercent}% off your next payment.`
    },
  },
  methods: {
    getPlanColor(subscription) {
      this.$emit('getPlanColor', subscription)
    },

    handleClickOnCancelSub() {
      if (this.isDisplayingNextInvoicePromotion) {
        this.$emit('showPromotionOnCancel')
        return
      }
      localStorage.setItem(storageKeys.IS_REMOVE_CARD_PURPOSE, false)
      this.isCancelSubInfo = true
    },
  },
}
</script>

<style lang="scss" scoped>
.table-plan {
  @apply w-full;
}
.table-plan-property {
  @apply font-bold py-2 w-1/2 md:w-1/3 align-top;
}
.table-plan-value {
  @apply py-2 w-1/2 md:w-2/3 align-top;
  word-break: break-word;
}
</style>
